import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";

export default function InvoicesPage() {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("toast.pdf_downloaded"));
    } catch { toast.error(t("toast.pdf_failed")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl font-bold">{t("invoices.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("invoices.subtitle")}</p>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("invoices.number")}</TableHead>
                <TableHead>{t("invoices.date")}</TableHead>
                <TableHead>{t("invoices.plan")}</TableHead>
                <TableHead className="text-right">{t("invoices.excl_vat")}</TableHead>
                <TableHead className="text-right">{t("invoices.vat")}</TableHead>
                <TableHead className="text-right">{t("invoices.total")}</TableHead>
                <TableHead>{t("invoices.status")}</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t("invoices.no_invoices")}</TableCell></TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow key={inv.id} className="table-row-hover" data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-mono-data text-sm font-medium">{inv.invoice_number}</TableCell>
                    <TableCell className="text-sm">{new Date(inv.created_at).toLocaleDateString("nl-NL")}</TableCell>
                    <TableCell>{inv.plan_name} <span className="text-muted-foreground text-xs capitalize">({inv.frequency})</span></TableCell>
                    <TableCell className="text-right font-mono-data">{inv.amount_excl_vat?.toFixed(2)} EUR</TableCell>
                    <TableCell className="text-right font-mono-data text-muted-foreground">{inv.vat_amount?.toFixed(2)} EUR</TableCell>
                    <TableCell className="text-right font-mono-data font-medium">{inv.total?.toFixed(2)} EUR</TableCell>
                    <TableCell>
                      <Badge variant={inv.status === "paid" ? "default" : "secondary"} className={inv.status === "paid" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>
                        {inv.status === "paid" ? t("invoices.paid") : t("invoices.open")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`download-pdf-${inv.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
